import { useEffect } from 'react';
import type { Question } from '../types.ts';
import { ProgressBar } from './ui.tsx';

interface Props {
  question: Question;
  selected: number | undefined;
  comment: string;
  onSelect: (level: number) => void;
  onSkip: () => void;
  onAdvance: () => void;
  onBack: () => void;
  onCommentChange: (text: string) => void;
  onCommentBlur: () => void;
  onBackToReview?: (() => void) | undefined;
  showBack: boolean;
  progress: { current: number; total: number };
}

export function QuestionScreen({
  question,
  selected,
  comment,
  onSelect,
  onSkip,
  onAdvance,
  onBack,
  onCommentChange,
  onCommentBlur,
  onBackToReview,
  showBack,
  progress,
}: Props) {
  // Scroll to top when question changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [progress.current]);

  // Keyboard: 1–5 select, Enter advance (SPEC § Style rules)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key >= '1' && e.key <= '5') {
        const level = Number(e.key);
        if (level <= question.anchors.length) {
          e.preventDefault();
          onSelect(level);
        }
      } else if (e.key === 'Enter' && selected !== undefined) {
        e.preventDefault();
        onAdvance();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [question, selected, onSelect, onAdvance]);

  return (
    <div>
      <ProgressBar current={progress.current} total={progress.total} />

      <h1 className="mt-6 text-xl font-semibold leading-tight text-ink md:text-2xl">
        {question.prompt}
      </h1>

      <div className="mt-5 flex flex-col gap-2" role="radiogroup" aria-label={question.prompt}>
        {[...question.anchors]
          .sort((a, b) => a.level - b.level)
          .map((anchor) => {
            const isSelected = selected === anchor.level;
            return (
              <button
                key={anchor.level}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => onSelect(anchor.level)}
                className="option-card flex w-full items-center gap-3 rounded-xl border bg-white px-4 py-2.5 text-left"
                style={{
                  borderColor: isSelected ? 'var(--accent)' : 'rgba(0,0,0,0.10)',
                  background: isSelected ? 'var(--accent-soft)' : 'white',
                }}
              >
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-sm font-bold"
                  style={{
                    background: isSelected ? 'var(--accent)' : 'rgba(0,0,0,0.05)',
                    color: isSelected ? 'white' : 'var(--color-ink-soft)',
                  }}
                >
                  {anchor.level}
                </span>
                <span className="text-[15px] leading-snug text-ink">{anchor.label}</span>
              </button>
            );
          })}
      </div>

      {/* Comment field */}
      <div className="mt-4">
        <label className="block text-xs font-medium text-ink-soft mb-1">
          Optional Comments
        </label>
        <textarea
          value={comment}
          onChange={(e) => onCommentChange(e.target.value)}
          onBlur={onCommentBlur}
          placeholder="Provide context for your rating..."
          className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-soft)] resize-y min-h-[72px]"
          maxLength={2000}
        />
      </div>

      <div className="mt-5 flex items-center justify-between gap-4">
        {onBackToReview ? (
          <button
            type="button"
            onClick={onBackToReview}
            className="text-sm font-medium text-ink-soft underline-offset-4 hover:underline"
          >
            ← Back to review
          </button>
        ) : showBack ? (
          <button
            type="button"
            onClick={onBack}
            className="text-sm font-medium text-ink-soft underline-offset-4 hover:underline"
          >
            ← Back
          </button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={onSkip}
            className="rounded-lg border border-black/15 px-3 py-2 text-sm font-medium text-ink-soft transition hover:border-black/30 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
          >
            {selected !== undefined ? 'Clear answer' : 'Skip this question'}
          </button>
          {selected !== undefined && (
            <button
              type="button"
              onClick={onAdvance}
              className="brand-gradient inline-flex items-center justify-center rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:brightness-105 active:scale-[0.98]"
            >
              Next →
            </button>
          )}
        </div>
      </div>
      <p className="mt-3 text-center text-xs text-ink-soft">
        Press 1–5 to choose · Enter to {onBackToReview ? 'return to review' : 'continue'}
      </p>
    </div>
  );
}
